import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;
const REQUEST_TIMEOUT_MS = 8000;
const SESSION_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatLoginError(error) {
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      return "Backend tidak merespons. Pastikan server backend di localhost:8000 sedang berjalan.";
    }
    return error.message || "Tidak bisa terhubung ke backend.";
  }
  return formatApiErrorDetail(error.response?.data?.detail);
}

function isAuthRejection(error) {
  return [401, 403].includes(error.response?.status);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let fallbackTimer;

    const finishBoot = (nextUser) => {
      if (!isMounted) return;
      window.clearTimeout(fallbackTimer);
      setUser(nextUser);
      setLoading(false);
    };

    const bootAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT_MS,
        });
        finishBoot(data);
      } catch (e) {
        finishBoot(false);
      }
    };

    fallbackTimer = window.setTimeout(() => {
      finishBoot(false);
    }, REQUEST_TIMEOUT_MS + 2000);

    bootAuth();

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true, timeout: REQUEST_TIMEOUT_MS }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatLoginError(e) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, timeout: REQUEST_TIMEOUT_MS });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(false);
    }
  };

  const refreshToken = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, timeout: REQUEST_TIMEOUT_MS });
      setUser((currentUser) => (data?.email ? data : currentUser));
      return true;
    } catch (e) {
      if (isAuthRejection(e)) {
        setUser(false);
      }
      return false;
    }
  };

  useEffect(() => {
    if (!user) return undefined;

    const refreshSession = () => {
      refreshToken();
    };

    const intervalId = window.setInterval(refreshSession, SESSION_REFRESH_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };
    const handleWindowFocus = () => {
      refreshSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [user]);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

